<template>
  <v-dialog v-model="addDialog" class="the-app-dialogs" persistent max-width="800px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
              <span class="px-3">{{ $t('Add New Stockholder') }}</span>
            </v-layout>
          </h3>

          <v-spacer />

          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout row wrap justify-end>
            <v-flex shrink>
              <v-chip v-if="fileSelected" :selected="fileSelected" @input="removeFile()" close dark color="primary">
                <v-avatar>
                  <v-icon>insert_drive_file</v-icon>
                </v-avatar>
                {{ fileName | truncate }}
              </v-chip>
              <v-btn :disabled="fileSelected" @click="chooseFile" color="primary" class="white--text">
                <v-icon left dark>attach_file</v-icon>
                {{ $t('Attach file') }}
              </v-btn>
              <input
                type="file"
                name="file"
                ref="fileInput"
                style="visibility:hidden; width:0; position:absolute; top:-9999px"
                @input="onSelectFile"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap justify-content-end>
            <v-flex xs12 pa-2>
              <v-text-field
                id="stockholder-focus-me"
                v-model="form.stockholder_name"
                :label="`${$t('Stockholder Name')} *`"
                name="stockholder_name"
                box
                hide-details
                background-color="white"
                :error="errors.has('stockholder_name')"
                v-validate="'required'"
              />
            </v-flex>

            <v-flex xs6 pa-2>
              <v-text-field
                v-model="form.number_of_shares"
                :label="`${$t('Number Of Shares')} *`"
                name="number_of_shares"
                box
                hide-details
                background-color="white"
                :error="errors.has('number_of_shares')"
                v-validate="'required|numeric'"
              />
            </v-flex>

            <v-flex xs6 pa-2 v-if="settings.opening_balance_status === 'Active'">
              <v-autocomplete
                v-model="form.cash_or_opening_balance"
                :label="`${$t('Cash / Opening Balance')} *`"
                hide-details
                :items="cashList"
                box
                data-vv-name="cash_or_opening_balance"
                background-color="white"
                :error="errors.has('cash_or_opening_balance')"
                v-validate="'required'"
              />
            </v-flex>

            <v-flex xs6 pa-2>
              <v-text-field
                v-model="form.amount"
                :label="`${$t('Amount')} (${systemCurrency}) *`"
                name="amount"
                box
                hide-details
                background-color="white"
                autocomplete="off"
                :error="errors.has('amount')"
                v-validate="'required|decimal|min_value:1'"
              />
            </v-flex>

            <v-flex xs6 class="pa-2">
              <v-menu v-model="dateMenu" full-width max-width="290" :close-on-content-click="false">
                <v-text-field
                  slot="activator"
                  :value="form.date"
                  :label="`${$t('Date')} *`"
                  prepend-inner-icon="event"
                  box
                  readonly
                  hide-details
                  name="date"
                  background-color="white"
                  autocomplete="off"
                  :error="errors.has('date')"
                  v-validate="'required|date_format:yyyy-MM-dd'"
                />
                <v-date-picker @change="dateMenu = false" v-model="form.date" />
              </v-menu>
            </v-flex>

            <v-flex xs12 class="pa-2">
              <v-textarea
                v-model="form.note"
                :label="$t('Note')"
                name="note"
                box
                hide-details
                height="80"
                background-color="white"
              />
            </v-flex>
          </v-layout>
        </v-card-title>

        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end align-center pr-2 pl-2>
              <v-checkbox color="primary" v-model="keepForm" :label="$t('Keep this form to add another one.')" />

              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnAddLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Add') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'add-component',
  props: {
    addDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      dateMenu: false,
      btnAddLoading: false,
      systemCurrency: localStorage.systemCurrency,
      form: {
        cash_or_opening_balance: '',
        date: ''
      },
      fileSelected: false,
      fileName: '',
      fileData: null,
      fileInput: null,
      theFile: null,
      keepForm: false
    }
  },

  computed: {
    ...mapGetters({
      baseUrls: 'misc/getBaseUrls',
      cashList: 'accountStatements/getCashOrOpenList',
      settings: 'configurations/getSystemSettings'
    })
  },

  watch: {
    addDialog: {
      immediate: false,
      handler() {
        if (this.addDialog) {
          this.addEscKeyListener()
          this.resetForm()
          this.keepForm = false
        }
      }
    }
  },

  methods: {
    ...mapActions({
      addStockholder: 'stockholders/addStockholder'
    }),

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the "ESC" button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.addDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    resetForm() {
      this.$validator.pause()
      this.removeFile()
      for (let key in this.form) {
        this.form[`${key}`] = null
      }
      this.form.cash_or_opening_balance = 'Cash'
      this.form.date = this.$moment()
        .format()
        .substr(0, 10)

      this.$nextTick(() => {
        this.$validator.resume()
        // default veutify autofocus only one time works so we have to handled in this way.
        // document.getElementById('stockholder-focus-me').focus()
      })
    },

    chooseFile() {
      this.$refs.fileInput.click()
    },

    onSelectFile() {
      this.fileInput = this.$refs.fileInput
      this.theFile = this.fileInput.files

      if (this.theFile && this.theFile[0]) {
        const reader = new FileReader()
        reader.onload = e => {
          this.fileData = e.target.result
        }
        reader.readAsDataURL(this.theFile[0])
        this.$emit('input', this.theFile[0])
        this.fileName = this.theFile[0].name
        this.fileSelected = true
      }
    },

    removeFile() {
      this.fileData = null
      this.fileInput = null
      this.theFile = null
      this.$refs.fileInput.value = ''
      this.fileName = ''
      this.fileSelected = false
    },

    validateBeforeSubmit(event) {
      this.$validator
        .validateAll()
        .then(() => {
          if (!this.errors.any()) {
            this.btnAddLoading = true

            let formData = new FormData(event.target)
            formData.append('cash_or_opening_balance', this.form.cash_or_opening_balance)
            if (this.fileData != null) {
              formData.append('attachment', this.theFile[0])
            }

            // for (let pair of formData.entries()) {
            //   console.warn(`${pair[0]}: ${pair[1]}`)
            // }

            this.addStockholder(formData)
              .then(() => {
                this.btnAddLoading = false
                this.$emit('reset-datatable')
                if (this.keepForm) {
                  this.resetForm()
                } else {
                  this.$emit('hidemodal')
                }
                this.fetchLastDollarPrice()
              })
              .catch(() => {
                this.btnAddLoading = false
              })
          }
        })
        .catch(() => {
          this.btnAddLoading = false
        })
    }
  }
}
</script>

<style scoped />
